import React, { useState, useCallback, useMemo, useEffect } from "react";
import SidebarContext from "./SidebarContext";

const SidebarContextProvider = ({
    children,
    entity = null,
    show = false,
    onSave,
    onDelete,
    onDiscard,
    metadata = null,
    initialPage = null,
}) => {
    const [currentEntity, setCurrentEntity] = useState(entity);
    const [showSidebar, setShowSidebar] = useState(show);
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [runtimeData, setRuntimeData] = useState({});
    const [emittedData, setEmittedData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Sync entity prop with state
    useEffect(() => {
        setCurrentEntity(entity);
    }, [entity]);

    // Sync show prop with state
    useEffect(() => {
        setShowSidebar(show);
    }, [show]);

    const handleSave = useCallback(async () => {
        if (!onSave) return;

        setIsSaving(true);
        try {
            await onSave(emittedData, currentEntity);
            // Reset state after successful save
            setRuntimeData({});
            setEmittedData({});
            setCurrentPage(initialPage);
            // Don't close sidebar here - let parent control via show prop
        } catch (error) {
            console.error("Error saving:", error);
            throw error;
        } finally {
            setIsSaving(false);
        }
    }, [onSave, emittedData, currentEntity, initialPage]);

    const handleDelete = useCallback(async () => {
        if (!onDelete || !currentEntity) return;

        setIsDeleting(true);
        try {
            await onDelete(currentEntity);
            // Reset state after successful delete
            setRuntimeData({});
            setEmittedData({});
            setCurrentPage(initialPage);
        } catch (error) {
            console.error("Error deleting:", error);
            throw error;
        } finally {
            setIsDeleting(false);
        }
    }, [onDelete, currentEntity, initialPage]);

    const handleDiscard = useCallback(() => {
        if (onDiscard) {
            onDiscard();
        }
        setShowSidebar(false);
        // Reset state
        setRuntimeData({});
        setEmittedData({});
        setCurrentPage(initialPage);
    }, [onDiscard, initialPage]);

    const handleNext = useCallback((nextPage) => {
        setCurrentPage(nextPage);
    }, []);

    const contextValue = useMemo(
        () => ({
            entity: currentEntity,
            setEntity: setCurrentEntity,
            show: showSidebar,
            setShow: setShowSidebar,
            currentPage,
            setCurrentPage,
            runtimeData,
            setRuntimeData,
            emittedData,
            setEmittedData,
            isSaving,
            isDeleting,
            onSave: handleSave,
            onDelete: handleDelete,
            onDiscard: handleDiscard,
            onNext: handleNext,
            metadata,
        }),
        [
            currentEntity,
            showSidebar,
            currentPage,
            runtimeData,
            emittedData,
            isSaving,
            isDeleting,
            handleSave,
            handleDelete,
            handleDiscard,
            handleNext,
            metadata,
        ]
    );

    return (
        <SidebarContext.Provider value={contextValue}>
            {children}
        </SidebarContext.Provider>
    );
};

export default SidebarContextProvider;
